/**
 * The provider stack, in one place because two entry points mount it and a
 * difference between them is a hydration mismatch.
 *
 * `main.tsx` renders it in the browser; `scripts/prerender.tsx` renders it at
 * build time to write the documents named in ./prerender-manifest.ts. Neither
 * composes the providers itself: a provider added to one and not the other
 * would change the tree's shape, React would throw the prerendered markup
 * away on arrival, and the only symptom would be a console error on a page
 * that still worked.
 *
 * The Convex client is a PROP rather than built here. The browser's comes
 * from `VITE_CONVEX_URL`; the prerender's is a placeholder that never
 * connects — see scripts/prerender.tsx. Passing it in keeps this component
 * free of the environment, which is the one thing the two callers genuinely
 * do not share.
 *
 * `RootErrorBoundary` sits innermost, around `children` alone: inside
 * LoggerProvider so its report carries the provider's correlation context,
 * and inside the theme and language providers so its fallback still has
 * them. A single wrapper adds no sibling, so no `useId` shifts; and both
 * entry points render it, so the tree's shape still matches.
 *
 * Everything here renders no DOM of its own except `ThemeProvider`, whose
 * inline script is the point of it: it sets the theme class before first
 * paint, and a prerendered document that shipped without it would flash the
 * wrong theme.
 */
import type { ReactNode } from "react";
import { ConvexProvider, type ConvexReactClient } from "convex/react";
import { ThemeProvider } from "@/components/theme-provider";
import { TargetLangProvider } from "@/components/target-lang-provider";
import { LoggerProvider } from "@/logging";
import { RootErrorBoundary } from "@/components/root-error-boundary";

export function AppProviders({
  client,
  children,
}: {
  client: ConvexReactClient;
  children: ReactNode;
}) {
  return (
    <ConvexProvider client={client}>
      <LoggerProvider>
        <ThemeProvider>
          <TargetLangProvider>
            <RootErrorBoundary>{children}</RootErrorBoundary>
          </TargetLangProvider>
        </ThemeProvider>
      </LoggerProvider>
    </ConvexProvider>
  );
}
